import Link from 'next/link';
import type { Project } from '@/lib/projects';
import { ProjectIcon } from '@/components/ui/ProjectIcon';
import { StatusBadge } from './StatusBadge';
import { StackTags } from './StackTags';

type Props = {
  project: Project;
};

function ExternalLinkIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M7 17 17 7" />
      <polyline points="8 7 17 7 17 16" />
    </svg>
  );
}

export function SmallProjectCard({ project }: Props) {
  const externalUrl = project.liveUrl ?? project.links?.[0]?.url;
  return (
    <article
      className="small-card group relative flex flex-col transition-all duration-[400ms] ease-out"
      style={
        {
          background: 'var(--bg-elevated)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-xl)',
          overflow: 'hidden',
          ['--project-accent' as string]: project.accentColor,
        } as React.CSSProperties
      }
    >
      {/* Stretched case-study link */}
      <Link
        href={`/projects/${project.slug}`}
        aria-label={`View ${project.name} case study`}
        className="absolute inset-0 z-0"
      />

      {/* Gradient hero band */}
      <div
        className="relative"
        aria-hidden
        style={{
          height: 88,
          background: project.gradient,
          backgroundImage: `${project.gradient}, radial-gradient(circle at 80% 20%, rgba(255,255,255,0.18) 0%, transparent 60%)`,
          backgroundBlendMode: 'soft-light',
          overflow: 'hidden',
        }}
      >
        <span
          aria-hidden
          className="font-display italic"
          style={{
            position: 'absolute',
            top: -16,
            right: -8,
            fontSize: 120,
            fontWeight: 700,
            color: 'rgba(255,255,255,0.12)',
            letterSpacing: '-0.05em',
            lineHeight: 1,
            userSelect: 'none',
          }}
        >
          {project.initials}
        </span>
        <span
          aria-hidden
          style={{
            position: 'absolute',
            inset: 0,
            background:
              'linear-gradient(180deg, transparent 0%, transparent 60%, rgba(13,13,22,0.6) 100%)',
          }}
        />
      </div>

      {/* External live-site icon */}
      {externalUrl && (
        <a
          href={externalUrl}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Open ${project.name} (opens in new tab)`}
          className="visit-site-icon absolute transition-colors"
          style={{
            top: 12,
            right: 12,
            color: 'rgba(255,255,255,0.8)',
            zIndex: 3,
            padding: 6,
            borderRadius: 8,
            background: 'rgba(0,0,0,0.25)',
            backdropFilter: 'blur(8px)',
            lineHeight: 0,
          }}
        >
          <ExternalLinkIcon />
        </a>
      )}

      <div className="relative flex flex-col gap-4 px-6 pt-5 pb-6" style={{ zIndex: 2 }}>
        <div className="flex items-start justify-between gap-3" style={{ marginTop: -28 }}>
          <ProjectIcon initials={project.initials} gradient={project.gradient} size={48} />
          <StatusBadge project={project} size="sm" />
        </div>

        <div>
          <h3
            className="font-sans text-text"
            style={{ fontSize: 17, fontWeight: 800, letterSpacing: '-0.01em' }}
          >
            {project.name}
          </h3>
          <p className="mt-1 text-text-secondary" style={{ fontSize: 13, lineHeight: 1.5 }}>
            {project.tagline}
          </p>
        </div>

        <div>
          <StackTags stack={project.stack} size="xs" limit={4} />
        </div>

        <div className="mt-auto pt-2 flex items-center justify-between gap-2">
          <span
            className="font-mono"
            style={{ fontSize: 10, color: 'var(--text-muted)', letterSpacing: '0.08em' }}
          >
            {project.categoryLabel.toUpperCase()}
          </span>
          <span
            className="case-link inline-flex items-center gap-1.5 font-sans"
            style={{ fontSize: 12, color: 'var(--accent)', fontWeight: 700 }}
          >
            Case study
            <span className="case-arrow transition-transform duration-300">→</span>
          </span>
        </div>
      </div>
    </article>
  );
}
